/**
 * Plan Instance response types for frontend consumption.
 *
 * These types represent the API response structure for plan instances.
 */

import type {
  ApplyPlanVersion,
  PlanInstanceOverrides,
  PlanVersionApplyMode,
  PlanInstanceStatus,
  PlanPeriodBaseline,
} from '../models/plan-instance.model';
import type { ConflictResult } from '../validation/conflict.types';

/**
 * Plan instance entity as returned by the API.
 *
 * A plan instance is `optional plan + concrete overrides`. `planVersionId` is
 * source provenance only and may be null after the source is deleted.
 */
export interface PlanInstance {
  /** Unique identifier for this plan instance */
  id: string;
  /** Plan this instance belongs to. Null for standalone instances. */
  planId: string | null;
  /** Source provenance. Null when no source was recorded or the source was deleted. */
  planVersionId: string | null;
  /** Strategy that provides the shift-unit catalogue. Always required. */
  strategyId: string;
  /** Start date of this instance (YYYY-MM-DD) */
  startDate: string;
  /** End date of this instance (YYYY-MM-DD) */
  endDate: string;
  /** Timezone for this instance (e.g., 'Europe/Zurich') */
  timezone: string;
  /** Lifecycle status: draft instances are only visible to planners, published instances are visible to employees */
  status: PlanInstanceStatus;
  /** Date-specific overrides for shift units */
  overrides: PlanInstanceOverrides;
  /** Pre-computed calendar conflicts (holidays, absences, closed days) */
  conflicts?: ConflictResult[] | null;
  /** Timestamp when conflicts were last computed */
  conflictsComputedAt?: string | null;
  /** User who created this instance */
  createdBy?: string | null;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last update timestamp (ISO 8601) */
  updatedAt: string;
}

/**
 * Query parameters for fetching plan instances.
 */
export interface PlanInstanceQueryParams {
  /** Filter by plan ID (required) */
  planId: string;
  /** Filter instances that overlap this start date */
  startDate?: string;
  /** Filter instances that overlap this end date */
  endDate?: string;
  /** Filter by status (draft | published) */
  status?: string;
}

/**
 * Query parameters for schedule expansion.
 */
export interface ScheduleQueryParams {
  /** Override start date (default: instance.startDate) */
  startDate?: string;
  /** Override end date (default: instance.endDate) */
  endDate?: string;
  /** When true, include calendar conflict data in the response */
  includeConflicts?: boolean;
}

export interface CreatePlanPeriodRequest {
  planId: string;
  startDate: string;
  endDate: string;
  timezone?: string;
  baseline?: PlanPeriodBaseline;
}

export interface PlanPeriodQueryParams {
  planId: string;
  startDate: string;
  endDate: string;
}

export type ApplyPlanVersionRequest = ApplyPlanVersion;

export interface ApplyPlanVersionAffectedRange {
  startDate: string;
  endDate: string;
  spanWeeks: number;
}

export interface ApplyPlanVersionConflictSummary {
  total: number;
  byType: Partial<Record<ConflictResult['type'], number>>;
}

export interface ApplyPlanVersionResponse {
  instance: PlanInstance;
  affectedRange: ApplyPlanVersionAffectedRange;
  conflicts: ConflictResult[];
  conflictSummary: ApplyPlanVersionConflictSummary;
}

export interface ApplyPlanVersionCommand {
  instanceId: string;
  planVersionId: string;
  targetWeekStartDate: string;
  mode: PlanVersionApplyMode;
  baseInstanceUpdatedAt: string;
}

/** Plan instance with no plan attached. Shift catalogue is anchored on `strategyId`. */
export type StandalonePlanInstance = PlanInstance & { planId: null; planVersionId: null };

/** Plan-backed instance; its concrete assignments remain in the instance snapshot. */
export type PlanBackedPlanInstance = PlanInstance & { planId: string };

/** Plan-backed instance with non-null source-version provenance. */
export type TemplateBackedPlanInstance = PlanBackedPlanInstance & { planVersionId: string };

/** Narrows a `PlanInstance` to a plan-backed variant, even when provenance is null. */
export function isPlanBacked(instance: PlanInstance): instance is PlanBackedPlanInstance {
  return instance.planId !== null;
}

/** Narrows a `PlanInstance` to its standalone variant. */
export function isStandalone(instance: PlanInstance): instance is StandalonePlanInstance {
  return instance.planId === null;
}

/** Narrows a `PlanInstance` to its template-backed variant. */
export function isTemplateBacked(instance: PlanInstance): instance is TemplateBackedPlanInstance {
  return instance.planId !== null && instance.planVersionId !== null;
}
